/**
 * Typography Components
 *
 * Reusable typography components with consistent styling for common text patterns.
 */
import React from "react";
import { Typography, TypographyProps } from "@mui/material";

// ================================
// Metric Text (Large numbers/values)
// ================================

interface MetricTextProps extends Omit<TypographyProps, "variant"> {
  value: string | number;
}

export const MetricText: React.FC<MetricTextProps> = ({
  value,
  sx,
  ...props
}) => (
  <Typography variant="h3" fontWeight={700} gutterBottom sx={sx} {...props}>
    {value}
  </Typography>
);

// ================================
// Label Text (Headers, titles)
// ================================

interface LabelTextProps extends Omit<TypographyProps, "variant"> {
  bold?: boolean;
}

export const LabelText: React.FC<LabelTextProps> = ({
  children,
  bold = true,
  sx,
  ...props
}) => (
  <Typography variant="body2" fontWeight={bold ? 600 : 400} sx={sx} {...props}>
    {children}
  </Typography>
);

// ================================
// Sub Text (Secondary information)
// ================================

export const SubText: React.FC<TypographyProps> = ({
  children,
  sx,
  ...props
}) => (
  <Typography variant="body2" color="text.secondary" sx={sx} {...props}>
    {children}
  </Typography>
);

// ================================
// Section Title (h6 with consistent styling)
// ================================

export const SectionTitle: React.FC<TypographyProps> = ({
  children,
  sx,
  ...props
}) => (
  <Typography variant="h6" fontWeight={600} sx={sx} {...props}>
    {children}
  </Typography>
);

// ================================
// Card Title (h6 for cards)
// ================================

export const CardTitle: React.FC<TypographyProps> = ({
  children,
  sx,
  ...props
}) => (
  <Typography variant="h6" component="div" fontWeight={600} sx={sx} {...props}>
    {children}
  </Typography>
);

// ================================
// Caption Text (Small helper text)
// ================================

export const CaptionText: React.FC<TypographyProps> = ({
  children,
  sx,
  ...props
}) => (
  <Typography variant="caption" color="text.secondary" sx={sx} {...props}>
    {children}
  </Typography>
);

const TypographyComponents = {
  MetricText,
  LabelText,
  SubText,
  SectionTitle,
  CardTitle,
  CaptionText,
};

export default TypographyComponents;
